import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";

interface PreviewConfig {
  isLocalPreview: boolean;
  localBranch: string | null;
  projectSlug: string | null;
  activeBranch: string | null;
  activeSource: string | null;
  ahead: number;
  behind: number;
  capabilities: string[];
  editMode: boolean;
  editBranch: string | null;
  autoFetchEnabled: boolean;
  lastFetchTimestamp: string | null;
  workspaceName: string | null;
  workspacePath: string | null;
}

interface PreviewContextValue extends PreviewConfig {
  currentBranch: string | null;
  previewConfigLoaded: boolean;
  refreshConfig: () => Promise<void>;
  setCurrentBranch: (branch: string | null) => void;
  setEditMode: (branch: string | null, enabled: boolean) => void;
}

const PreviewContext = createContext<PreviewContextValue>({
  isLocalPreview: false,
  localBranch: null,
  projectSlug: null,
  activeBranch: null,
  activeSource: null,
  ahead: 0,
  behind: 0,
  capabilities: [],
  editMode: false,
  editBranch: null,
  autoFetchEnabled: false,
  lastFetchTimestamp: null,
  workspaceName: null,
  workspacePath: null,
  currentBranch: null,
  previewConfigLoaded: false,
  refreshConfig: async () => {},
  setCurrentBranch: () => {},
  setEditMode: () => {},
});



export function PreviewProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<PreviewConfig>({
    isLocalPreview: false,
    localBranch: null,
    projectSlug: null,
    activeBranch: null,
    activeSource: null,
    ahead: 0,
    behind: 0,
    capabilities: [],
    editMode: false,
    editBranch: null,
    autoFetchEnabled: false,
    lastFetchTimestamp: null,
    workspaceName: null,
    workspacePath: null,
  });

  const [currentBranch, setCurrentBranchState] = useState<string | null>(null);
  const [previewConfigLoaded, setPreviewConfigLoaded] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const r = await fetch("/api/preview-config");
      if (!r.ok) { setPreviewConfigLoaded(true); return; }
      const data = await r.json();
      if (data?.isLocalPreview) {
        // Inject tokens into localStorage if present (from ~/.cortex/credentials)
        if (data.accessToken) {
          localStorage.setItem("cortex_access_token", data.accessToken);
        }
        if (data.refreshToken) {
          localStorage.setItem("cortex_refresh_token", data.refreshToken);
        }
        // Notify AuthContext to re-read tokens
        if (data.accessToken) {
          window.dispatchEvent(new CustomEvent("preview-tokens-injected"));
        }
        setConfig((prev) => ({
          isLocalPreview: true,
          localBranch: data.branch || null,
          projectSlug: data.projectSlug || null,
          activeBranch: data.activeBranch || null,
          activeSource: data.activeSource || "local",
          ahead: data.ahead || 0,
          behind: data.behind || 0,
          capabilities: data.capabilities || [],
          editMode: prev.editMode,
          editBranch: prev.editBranch,
          autoFetchEnabled: data.autoFetchEnabled || false,
          lastFetchTimestamp: data.lastFetchTimestamp || null,
          workspaceName: data.workspaceName || null,
          workspacePath: data.workspacePath || null,
        }));
      }
    } catch {
      // Not running behind preview server — production mode
    } finally {
      setPreviewConfigLoaded(true);
    }
  }, []);

  // The preview-config endpoint is only ever served by the local preview server
  // (localhost). On a real domain it 404s on every page load — skip the probe
  // there and just mark config loaded, so production never fires the dead request.
  useEffect(() => {
    const h = window.location.hostname;
    if (h === "localhost" || h === "127.0.0.1") fetchConfig();
    else setPreviewConfigLoaded(true);
  }, [fetchConfig]);

  // Subscribe to SSE reload events from the preview server.
  // CLI commands (commit, pull, switch, reset) trigger these.
  useEffect(() => {
    if (!config.isLocalPreview) return;
    let es: EventSource | null = null;
    try {
      es = new EventSource("/api/preview/events");
      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Forward all typed events from the preview server to data-fetching hooks.
          // Known types: reload, branch-switch, commit, content-change, stage, unstage, reset, restore
          if (data.type && data.type !== "connected") {
            // Await fetchConfig so PreviewContext state (activeBranch, ahead/behind)
            // is updated BEFORE downstream hooks (useCms) process the event.
            fetchConfig().finally(() => {
              window.dispatchEvent(new CustomEvent("preview-reload", { detail: { type: data.type } }));
            });
          }
        } catch {
          // ignore parse errors
        }
      };
      es.onerror = () => {
        // SSE connection lost — preview server might have restarted
        es?.close();
      };
    } catch {
      // EventSource not supported or URL issue
    }
    return () => { es?.close(); };
  }, [config.isLocalPreview, fetchConfig]);

  const setEditMode = useCallback((branch: string | null, enabled: boolean) => {
    setConfig((prev) => ({
      ...prev,
      editMode: enabled,
      editBranch: enabled ? branch : null,
    }));
  }, []);

  const setCurrentBranch = useCallback((branch: string | null) => {
    setCurrentBranchState(branch);
    // Update URL query param for peek
    const url = new URL(window.location.href);
    if (branch && branch !== config.activeBranch) {
      url.searchParams.set("peek", branch);
    } else {
      url.searchParams.delete("peek");
    }
    window.history.replaceState(null, "", url.pathname + url.search);
  }, [config.activeBranch]);

  return (
    <PreviewContext.Provider value={{ ...config, currentBranch, previewConfigLoaded, refreshConfig: fetchConfig, setCurrentBranch, setEditMode }}>
      {children}
    </PreviewContext.Provider>
  );
}

export function usePreview(): PreviewContextValue {
  return useContext(PreviewContext);
}

/**
 * Returns true when the current view is read-only.
 * Read-only when:
 * - activeSource is "remote" (previewing a remote branch — always read-only)
 * - the active branch is "main" (main is protected — use a feature branch)
 * - peeking at a non-active branch without edit mode
 * - viewing an origin/ branch (always read-only)
 */
export function useIsReadOnly(): boolean {
  const { currentBranch, activeBranch, activeSource, editMode } = usePreview();
  // Remote branch preview is always read-only
  if (activeSource === "remote") return true;
  // Main branch is always read-only — edits must go through feature branches
  const effectiveBranch = currentBranch || activeBranch;
  if (effectiveBranch === "main") return true;
  // Not peeking — normal mode
  if (!currentBranch || currentBranch === activeBranch) return false;
  // origin/ branches are always read-only
  if (currentBranch.startsWith("origin/")) return true;
  // Peeking without edit mode = read-only
  return !editMode;
}
